/**
 * Story Vertical Slider - Full Page Parallax Effect
 * - New slide slides up from bottom to overlay current slide
 * - Current slide moves up slightly for parallax effect
 * - Comet drops from top to middle, flies up on scroll, drops again on new slide
 */
class StoryVerticalSlider {
  constructor() {
    this.isInitialized = false;
    this.currentSlide = 0;
    this.totalSlides = 0;
    /** @type {HTMLElement[]} */
    this.slides = [];
    /** @type {HTMLElement | null} */
    this.container = null;
    /** @type {HTMLElement | null} */
    this.cometElement = null;
    this.isTransitioning = false;
    // Duration of slide transition (must match --slide-transition in CSS, currently 1.5s)
    this.slideDuration = 1000; // ms - transition duration
    this.scrollThreshold = 50;
    this.parallaxOffset = 15; // vh - how much current slide moves up
    this.accumulatedDelta = 0;
    this.isSliderActive = false;
    // Track comet animations
    this.cometDownTimeout = 2000; // Track comet down animation timeout
    /** @type {number} */
    this.cometHideTimeout = 0; // Hide comet on last slide after delay
    // Body scroll locking
    this.bodyScrollLocked = false;
    this.originalBodyOverflow = '';
    this.lastSlideChangeTime = 0;
    this.slideChangeCooldown = this.slideDuration + 500; // ms
  }

  init() {
    if (this.isInitialized) return;

    const mainContent = document.getElementById('MainContent');
    if (!mainContent) {
      console.warn('MainContent not found');
      return;
    }

    // Find original comet element to clone into each slide
    const originalCometContent = document.querySelector('.comet-content');
    if (!originalCometContent) {
      console.warn('Comet element not found');
    }

    // Find all story-section shopify-sections
    const storySections = Array.from(mainContent.querySelectorAll('.shopify-section')).filter(
      section => section.querySelector('story-section-animation')
    );

    if (storySections.length === 0) {
      console.warn('No story sections found');
      return;
    }

    console.log('Story Vertical Slider: Found', storySections.length, 'story sections');

    this.totalSlides = storySections.length;

    // Create wrapper - takes up exactly viewport height
    const wrapper = document.createElement('div');
    wrapper.className = 'story-parallax-container';
    this.container = wrapper;

    /** @type {HTMLElement[]} */
    this.cometElements = [];

    // Convert sections to parallax slides
    storySections.forEach((section, index) => {
      const slide = document.createElement('div');
      slide.className = 'story-parallax-slide';
      slide.setAttribute('data-slide-index', String(index));

      // Clone the section content
      slide.appendChild(section.cloneNode(true));

      // Clone comet into each slide (between background and content)
      if (originalCometContent) {
        const cometClone = originalCometContent.cloneNode(true);
        cometClone.classList.add('slide-comet');
        // Find the story-section or story-block to insert comet
        const storySection = slide.querySelector('.story-section, .story-block');
        if (storySection) {
          // Insert comet after background, before content wrapper
          const contentWrapper = storySection.querySelector('.story-content-wrapper');
          if (contentWrapper) {
            storySection.insertBefore(cometClone, contentWrapper);
          } else {
            storySection.appendChild(cometClone);
          }
        }
        this.cometElements.push(cometClone);
      }

      // First slide is active, rest are below viewport
      if (index === 0) {
        slide.classList.add('is-active');
      } else {
        slide.classList.add('is-below');
      }

      wrapper.appendChild(slide);
      this.slides.push(slide);

      // Remove original section
      section.remove();
    });

    // Remove the original comet section since we cloned it into slides
    const originalCometWrapper = document.querySelector('.comet-wrapper');
    if (originalCometWrapper) {
      const cometSection = originalCometWrapper.closest('.shopify-section');
      if (cometSection) {
        cometSection.remove();
      } else {
        originalCometWrapper.remove();
      }
    }

    // Set reference to comet in first slide (for animation control)
    this.cometElement = this.cometElements[0] || null;

    // Insert wrapper at the beginning of MainContent
    mainContent.insertBefore(wrapper, mainContent.firstChild);

    // Setup scroll/wheel control
    this.setupScrollControl();

    // Animate first slide content
    setTimeout(() => {
      const firstSlide = this.slides[0];
      if (firstSlide) {
        this.animateSlideContent(firstSlide);
      }
      this.animateCometDown();
    }, 1000);

    this.isInitialized = true;

    // Ensure correct body scroll state on load
    this.updateBodyScrollLock();
  }

  /**
   * Lock body scroll so the user can't scroll to the footer
   * while navigating between slides.
   */
  lockBodyScroll() {
    if (this.bodyScrollLocked) return;
    this.bodyScrollLocked = true;
    this.originalBodyOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('lock-scroll');
  }

  /**
   * Restore body scroll to its previous state.
   */
  unlockBodyScroll() {
    if (!this.bodyScrollLocked) return;
    this.bodyScrollLocked = false;
    document.body.style.overflow = this.originalBodyOverflow;
      document.body.classList.remove('lock-scroll');
  }

  /**
   * Update body scroll lock based on current slide and viewport position.
   * - Locked when slider is in viewport and not on the last slide.
   * - Unlocked when on the last slide or slider is out of view.
   */
  updateBodyScrollLock() {
    if (!this.container) {
      this.unlockBodyScroll();
      return;
    }

    if (!this.isSliderInViewport()) {
      this.unlockBodyScroll();
      return;
    }

    const atLast = this.currentSlide === this.totalSlides - 1;
    if (atLast) {
      this.unlockBodyScroll();
    } else {
      this.lockBodyScroll();
    }
  }

  /**
   * Get the comet element for a specific slide index
   * @param {number} slideIndex
   * @returns {HTMLElement | null}
   */
  getCometForSlide(slideIndex) {
    return this.cometElements[slideIndex] || null;
  }

  /**
   * Reset comet to initial hidden state (no animation classes)
   * @param {HTMLElement} comet
   */
  resetComet(comet) {
    if (!comet) return;
    comet.style.top = '';
    comet.classList.remove('is-up', 'is-down', 'is-hidden');
  }

  animateCometDown() {
    // Only animate comet in the CURRENT active slide
    const currentComet = this.getCometForSlide(this.currentSlide);
    if (!currentComet) return;

    // Clear any pending comet down animation
    if (this.cometDownTimeout) {
      clearTimeout(this.cometDownTimeout);
      this.cometDownTimeout = null;
    }

    // Clear any pending hide timeout
    if (this.cometHideTimeout) {
      clearTimeout(this.cometHideTimeout);
      this.cometHideTimeout = 0;
    }

    // Clear any inline styles from previous animations
    currentComet.style.top = '';
    // Remove all classes first
    currentComet.classList.remove('is-up', 'is-down');
    // Force reflow to ensure transition restarts
    void currentComet.offsetHeight;
    // Add is-down class
    currentComet.classList.add('is-down');

    // If we're on the last slide, schedule comet to fully hide after 3s
    const isLastSlide = this.currentSlide === this.totalSlides - 1;
    if (isLastSlide) {
      this.cometHideTimeout = window.setTimeout(() => {
        const lastComet = this.getCometForSlide(this.totalSlides - 1);
        if (lastComet) {
          lastComet.classList.add('is-hidden');
        }
      }, 3000);
    }
  }

  /**
   * Animate comet up for a specific slide (when leaving that slide)
   * @param {number} slideIndex - The slide index whose comet should fly up
   */
  animateCometUpForSlide(slideIndex) {
    const comet = this.getCometForSlide(slideIndex);
    if (!comet) return;

    // Clear any pending comet down animation to prevent it from triggering
    if (this.cometDownTimeout) {
      clearTimeout(this.cometDownTimeout);
      this.cometDownTimeout = null;
    }

    // If we're leaving the last slide, cancel any scheduled hide timeout
    if (this.cometHideTimeout && slideIndex === this.totalSlides - 1) {
      clearTimeout(this.cometHideTimeout);
      this.cometHideTimeout = 0;
    }

    // Get the current computed position before changing anything
    const computedStyle = window.getComputedStyle(comet);
    const currentTop = computedStyle.top;

    // Freeze the current position with inline style so it doesn't jump
    comet.style.top = currentTop;

    // Remove is-down and add is-up
    comet.classList.remove('is-down');
    comet.classList.add('is-up');

    // Force reflow to ensure browser registers the frozen state
    void comet.offsetHeight;

    // Remove inline style - the element will animate from current position to -100%
    comet.style.top = '';
  }

  animateCometUp() {
    // Animate comet up for the CURRENT slide (before transition)
    this.animateCometUpForSlide(this.currentSlide);
  }

  /**
   * @param {HTMLElement} slide
   */
  animateSlideContent(slide) {
    const heading = slide.querySelector('[data-story-animation="heading"]');
    const description = slide.querySelector('[data-story-animation="description"]');
    const subheading = slide.querySelector('[data-story-animation="subheading"]');

    if (heading) {
      heading.classList.add('animate-in');
    }
    if (subheading) {
      subheading.classList.add('animate-in');
    }
    if (description) {
      description.classList.add('animate-in');
    }
  }

  /**
   * @param {HTMLElement} slide
   */
  resetSlideContent(slide) {
    const heading = slide.querySelector('[data-story-animation="heading"]');
    const description = slide.querySelector('[data-story-animation="description"]');
    const subheading = slide.querySelector('[data-story-animation="subheading"]');

    if (heading) {
      heading.classList.remove('animate-in');
    }
    if (subheading) {
      subheading.classList.remove('animate-in');
    }
    if (description) {
      description.classList.remove('animate-in');
    }
  }

  /**
   * Check if slider should handle scroll (when container is in viewport)
   * @returns {boolean}
   */
  isSliderInViewport() {
    if (!this.container) return false;
    const rect = this.container.getBoundingClientRect();
    // Slider is in viewport if its top is at viewport top (within 5px)
    // and its bottom is still below the viewport
    return rect.top <= 5 && rect.top >= -5 && rect.bottom > window.innerHeight * 0.5;
  }

  setupScrollControl() {
    /**
     * Handle mouse wheel / trackpad scroll
     * @param {WheelEvent} e
     */
    const handleWheel = (e) => {
      const now = Date.now();

      if (now - this.lastSlideChangeTime < this.slideChangeCooldown) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Don't handle if transitioning
      if (this.isTransitioning) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Check if slider is in viewport
      if (!this.isSliderInViewport()) {
        // Reset delta when slider is not in view
        this.accumulatedDelta = 0;
        // Ensure body scroll is restored when leaving slider
        this.unlockBodyScroll();
        return; // Let browser handle scroll
      }

      const atFirst = this.currentSlide === 0;
      const atLast = this.currentSlide === this.totalSlides - 1;

      // When slider is in view and not at last slide, lock body scroll so we
      // can't scroll down to the footer. When on last slide, restore it.
      if (!atLast) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }

      // Scroll down (deltaY > 0)
      if (e.deltaY > 0) {
        if (!atLast) {
          // Not at last slide - prevent scroll and change slide
          e.preventDefault();
          e.stopPropagation();
          this.accumulatedDelta += e.deltaY;

          if (this.accumulatedDelta >= this.scrollThreshold) {
            this.accumulatedDelta = 0;
            this.goToNextSlide();
          }
        }
        // At last slide - allow browser scroll to footer
        return;
      }

      // Scroll up (deltaY < 0)
      if (e.deltaY < 0) {
        if (!atFirst) {
          // Check if container is exactly at top (within 5px tolerance)
          const rect = this.container.getBoundingClientRect();
          const isAtTop = rect.top <= 5 && rect.top >= -5;

          if (isAtTop) {
            // At top of container - prevent scroll and change slide
            e.preventDefault();
            e.stopPropagation();
            this.accumulatedDelta += e.deltaY;

            if (this.accumulatedDelta <= -this.scrollThreshold) {
              this.accumulatedDelta = 0;
              this.goToPrevSlide();
            }
          }
          // Not at top yet - allow browser scroll to bring container to top
        }
        // At first slide - allow browser scroll up
        return;
      }
    };

    // Add wheel listener to window to capture all scroll events
    window.addEventListener('wheel', handleWheel, { passive: false });

    /**
     * Handle keyboard navigation (PageUp/PageDown and ArrowUp/ArrowDown)
     * @param {KeyboardEvent} e
     */
    const handleKeyDown = (e) => {
      // Don't handle if transitioning
      if (this.isTransitioning) return;

      // Only handle when slider is in viewport
      if (!this.isSliderInViewport()) return;

      // Ignore when typing in inputs/textareas/selects or contenteditable elements
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.tagName === 'SELECT' ||
          activeEl.isContentEditable)
      ) {
        return;
      }

      const atFirst = this.currentSlide === 0;
      const atLast = this.currentSlide === this.totalSlides - 1;

      // Next slide: PageDown or ArrowDown
      if ((e.key === 'PageDown' || e.key === 'ArrowDown') && !atLast) {
        e.preventDefault();
        e.stopPropagation();
        // While navigating inside slider (not last slide), keep body locked
        this.lockBodyScroll();
        this.goToNextSlide();
        // If we just moved onto the last slide, update body scroll state
        this.updateBodyScrollLock();
        return;
      }

      // Previous slide: PageUp or ArrowUp
      if ((e.key === 'PageUp' || e.key === 'ArrowUp') && !atFirst) {
        e.preventDefault();
        e.stopPropagation();
        this.goToPrevSlide();
        this.updateBodyScrollLock();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Handle touch events for mobile
    let touchStartY = 0;
    let touchEndY = 0;

    window.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      if (touch) {
        // Reset both start và end để lần chạm mới không dùng lại diff cũ.
        touchStartY = touch.clientY;
        touchEndY = touch.clientY;
      }
    }, { passive: true });

    window.addEventListener('touchmove', (e) => {
      if (this.isTransitioning) {
        e.preventDefault();
        return;
      }

      if (!this.isSliderInViewport()) return;

      const touch = e.touches[0];
      if (!touch) return;
      touchEndY = touch.clientY;
      const diff = touchStartY - touchEndY;

      const atFirst = this.currentSlide === 0;
      const atLast = this.currentSlide === this.totalSlides - 1;

      // Swipe up (next slide) - prevent default scroll
      if (diff > 0 && !atLast) {
        e.preventDefault();
      }

      // Swipe down (prev slide) - prevent default scroll
      if (diff < 0 && !atFirst) {
        e.preventDefault();
      }
    }, { passive: false });

    window.addEventListener('touchend', () => {
      if (this.isTransitioning) return;
      if (!this.isSliderInViewport()) return;

      const diff = touchStartY - touchEndY;
      const now = Date.now();
      if (now - this.lastSlideChangeTime < this.slideChangeCooldown) {
        touchStartY = 0;
        touchEndY = 0;
        return;
      }
      const atFirst = this.currentSlide === 0;
      const atLast = this.currentSlide === this.totalSlides - 1;

      // Swipe up (next slide)
      if (diff > 50 && !atLast) {
        this.goToNextSlide();
      }

      // Swipe down (prev slide)
      if (diff < -50 && !atFirst) {
        this.goToPrevSlide();
      }

      touchStartY = 0;
      touchEndY = 0;
    }, { passive: true });
  }

  goToNextSlide() {
    const currentSlideEl = this.slides[this.currentSlide];
    const nextSlideEl = this.slides[this.currentSlide + 1];
    const currentSlideIndex = this.currentSlide;
    const nextSlideIndex = this.currentSlide + 1;

    if (!nextSlideEl || !currentSlideEl) {
      return;
    }

    this.isTransitioning = true;
    this.lastSlideChangeTime = Date.now();

    // Check if next slide is the last one
    const isNextLastSlide = nextSlideIndex === this.totalSlides - 1;

    // Animate comet of CURRENT slide up (before we change currentSlide)
    this.animateCometUpForSlide(currentSlideIndex);

    // Reset comet of NEXT slide to hidden state (so it doesn't show during transition)
    const nextComet = this.getCometForSlide(nextSlideIndex);
    if (nextComet) {
      this.resetComet(nextComet);
    }

    // Current slide: add parallax (move up slightly)
    currentSlideEl.classList.remove('is-active');
    currentSlideEl.classList.add('is-above');

    // Next slide: slide up from below
    nextSlideEl.classList.remove('is-below');
    nextSlideEl.classList.add('is-active', 'is-entering');

    // Update index
    this.currentSlide++;

    // Update body scroll lock based on new slide position
    this.updateBodyScrollLock();

    // After slide transition completes
    setTimeout(() => {
      nextSlideEl.classList.remove('is-entering');

      // Animate content
      this.animateSlideContent(nextSlideEl);

      // Schedule comet down animation for the NEW active slide
      this.cometDownTimeout = setTimeout(() => {
        this.animateCometDown();
        // Comet will naturally go behind .story-center-image on the last slide
        // due to z-index (comet: 5, .story-center-image: 6)
        this.cometDownTimeout = null;
      }, 100);

      this.isTransitioning = false;
    }, this.slideDuration);
  }

  goToPrevSlide() {
    const currentSlideEl = this.slides[this.currentSlide];
    const prevSlideEl = this.slides[this.currentSlide - 1];
    const currentSlideIndex = this.currentSlide;
    const prevSlideIndex = this.currentSlide - 1;

    if (!prevSlideEl || !currentSlideEl) {
      return;
    }

    this.isTransitioning = true;
    this.lastSlideChangeTime = Date.now();

    // Animate comet of CURRENT slide up (before we change currentSlide)
    this.animateCometUpForSlide(currentSlideIndex);

    // Reset comet of PREV slide to hidden state (so it doesn't show during transition)
    const prevComet = this.getCometForSlide(prevSlideIndex);
    if (prevComet) {
      this.resetComet(prevComet);
    }

    // Reset current slide content
    this.resetSlideContent(currentSlideEl);

    // Current slide: slide back down
    currentSlideEl.classList.remove('is-active');
    currentSlideEl.classList.add('is-below', 'is-leaving');

    // Previous slide: remove parallax, become active
    prevSlideEl.classList.remove('is-above');
    prevSlideEl.classList.add('is-active');

    // Update index
    this.currentSlide--;

    // Update body scroll lock based on new slide position
    this.updateBodyScrollLock();

    // After transition completes
    setTimeout(() => {
      currentSlideEl.classList.remove('is-leaving');

      // Schedule comet down animation
      this.cometDownTimeout = setTimeout(() => {
        this.animateCometDown();
        this.cometDownTimeout = null;
      }, 100);

      this.isTransitioning = false;
    }, this.slideDuration);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const slider = new StoryVerticalSlider();
    slider.init();
  });
} else {
  const slider = new StoryVerticalSlider();
  slider.init();
}
